import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Lockup } from './Logo';

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';

const Page = styled.div`
  min-height: 100vh;
  background: var(--bg-0);
  color: var(--text-0);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 18px;
`;

const Shell = styled.div`
  width: min(760px, 100%);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
`;

const Card = styled.div`
  background: var(--bg-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 28px;
`;

const Eyebrow = styled.div`
  color: var(--accent);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 8px 0 8px;
  font-size: 30px;
  line-height: 1.15;
`;

const Copy = styled.p`
  margin: 0 0 22px;
  color: var(--text-2);
  font-size: 14px;
  line-height: 1.6;
`;

const Progress = styled.div`
  height: 4px;
  background: var(--bg-3);
  border-radius: 99px;
  overflow: hidden;
  margin: 18px 0 26px;
  div { height: 100%; background: var(--accent); transition: width .25s ease; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 11px;
  font-family: var(--font-mono);
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: .04em;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-2);
  color: var(--text-0);
  padding: 11px 12px;
  font: 13px var(--font-body);
  text-transform: none;
  letter-spacing: normal;
  &:focus { outline: none; border-color: var(--accent); }
`;

const Button = styled.button`
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: var(--accent);
  color: white;
  padding: 10px 17px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: .45; cursor: not-allowed; }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: var(--text-1);
  border-color: var(--border);
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
`;

const Summary = styled.div`
  background: var(--bg-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 18px;
  margin-top: 16px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 14px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Metric = styled.div`
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 12px;
  span { display: block; color: var(--text-2); font-size: 10px; text-transform: uppercase; font-family: var(--font-mono); }
  strong { display: block; margin-top: 5px; font-size: 20px; }
`;

const Notice = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid ${props => props.$error ? 'var(--red)' : 'var(--border)'};
  color: ${props => props.$error ? 'var(--red)' : 'var(--text-1)'};
  border-radius: 6px;
  font-size: 12px;
`;

function authHeaders(token, json = false) {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${token}`,
  };
}

export default function Onboarding({ authToken, initialOrganization, initialOnboarding, onComplete, onLogout }) {
  const [organization, setOrganization] = useState(initialOrganization || null);
  const [onboarding, setOnboarding] = useState(initialOnboarding || null);
  const [profile, setProfile] = useState(null);
  const [companyName, setCompanyName] = useState(initialOrganization?.name || '');
  const [websiteUrl, setWebsiteUrl] = useState(initialOrganization?.websiteUrl || '');
  const [naics, setNaics] = useState('');
  const [agencies, setAgencies] = useState('');
  const [verified, setVerified] = useState(false);
  const [facilityClearance, setFacilityClearance] = useState('unknown');
  const [availableStaff, setAvailableStaff] = useState('');
  const [preferredRole, setPreferredRole] = useState('either');
  const [operatingLimitations, setOperatingLimitations] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setOrganization(initialOrganization || null);
    setOnboarding(initialOnboarding || null);
  }, [initialOrganization, initialOnboarding]);

  useEffect(() => {
    if (!organization) return;
    fetch(`${BASE_URL}/api/profile`, { headers: authHeaders(authToken) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const p = data?.profile;
        if (!p) return;
        setProfile(p);
        setFacilityClearance(p.facilityClearance || 'unknown');
        setAvailableStaff(p.availableStaff ?? '');
        setPreferredRole(p.preferredRole || 'either');
        setOperatingLimitations(p.operatingLimitations || '');
        setCompanyName(p.companyName || organization.name || '');
        setNaics((p.naicsCodes || []).join(', '));
        setAgencies((p.targetAgencies || []).join(', '));
      })
      .catch(() => {});
  }, [organization, authToken]);

  const step = !organization ? 1 : !onboarding?.profileGenerated || !onboarding?.targetingConfigured ? 2 : 3;
  const progress = step === 1 ? 20 : step === 2 ? 55 : 85;
  const splitList = value => value.split(',').map(v => v.trim()).filter(Boolean);
  const canBuild = companyName.trim() && websiteUrl.trim() && splitList(naics).length && splitList(agencies).length;
  const summary = useMemo(() => ({
    capabilities: profile?.coreCapabilities?.length || 0,
    corporatePastPerformance: profile?.corporatePastPerformance?.length || 0,
    keyPersonnel: profile?.keyPersonnel?.length || 0,
  }), [profile]);

  const createWorkspace = async () => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${BASE_URL}/api/organization`, {
        method: 'POST',
        headers: authHeaders(authToken, true),
        body: JSON.stringify({ name: companyName.trim(), websiteUrl: websiteUrl.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not create workspace');
      setOrganization(data.organization);
      setOnboarding(data.onboarding);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const buildProfile = async () => {
    setBusy(true); setError('');
    try {
      await fetch(`${BASE_URL}/api/organization`, {
        method: 'PATCH', headers: authHeaders(authToken, true),
        body: JSON.stringify({ name: companyName.trim(), websiteUrl: websiteUrl.trim() }),
      });
      const generatedResponse = await fetch(`${BASE_URL}/api/profile/generate`, {
        method: 'POST', headers: authHeaders(authToken, true),
        body: JSON.stringify({ websiteUrl: websiteUrl.trim() }),
      });
      const generated = await generatedResponse.json();
      if (!generatedResponse.ok) throw new Error(generated.error || 'Could not build company intelligence');

      const savedResponse = await fetch(`${BASE_URL}/api/profile`, {
        method: 'PUT', headers: authHeaders(authToken, true),
        body: JSON.stringify({
          companyName: companyName.trim(),
          naicsCodes: splitList(naics),
          targetAgencies: splitList(agencies),
        }),
      });
      const saved = await savedResponse.json();
      if (!savedResponse.ok) throw new Error(saved.error || 'Could not save targeting');
      setProfile(saved.profile);
      const stateResponse = await fetch(`${BASE_URL}/api/onboarding`, { headers: authHeaders(authToken) });
      const state = await stateResponse.json();
      setOnboarding(state.onboarding);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const launch = async () => {
    setBusy(true); setError('');
    try {
      const profileResponse = await fetch(`${BASE_URL}/api/profile`, {
        method: 'PUT', headers: authHeaders(authToken, true),
        body: JSON.stringify({ facilityClearance, availableStaff: availableStaff === '' ? null : Number(availableStaff), preferredRole, operatingLimitations }),
      });
      if (!profileResponse.ok) throw new Error('Could not save company execution limits');
      const verifyResponse = await fetch(`${BASE_URL}/api/onboarding`, {
        method: 'PATCH', headers: authHeaders(authToken, true),
        body: JSON.stringify({ truthModelVerified: true }),
      });
      const verifyData = await verifyResponse.json();
      if (!verifyResponse.ok) throw new Error(verifyData.error || 'Could not verify profile');
      const completeResponse = await fetch(`${BASE_URL}/api/onboarding/complete`, {
        method: 'POST', headers: authHeaders(authToken, true),
      });
      const completeData = await completeResponse.json();
      if (!completeResponse.ok) throw new Error(completeData.error || 'Could not launch workspace');
      setOnboarding(completeData.onboarding);
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <Lockup iconSize={30} wordSize={22} />
          <SecondaryButton type="button" onClick={onLogout}>Sign out</SecondaryButton>
        </Header>
        <Card>
          <Eyebrow>Workspace setup · Step {step} of 3</Eyebrow>
          <Title>{step === 1 ? 'Create your company workspace' : step === 2 ? 'Build your company intelligence' : 'Verify your Truth Model'}</Title>
          <Copy>
            {step === 1 && 'This workspace becomes the private home for your Company Truth Model and setup state—the foundation SAMKLR will use to personalize scoring, pursuits, drafts, and team access.'}
            {step === 2 && 'SAMKLR will analyze your website, combine it with your targeting preferences, and create the first version of your Company Truth Model.'}
            {step === 3 && 'Review the generated foundation before SAMKLR uses it for opportunity scoring and proposal claims. You can refine every field from Profile after launch.'}
          </Copy>
          <Progress><div style={{ width: `${progress}%` }} /></Progress>

          {(step === 1 || step === 2) && (
            <Grid>
              <Field>Company name<Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Federal Systems" /></Field>
              <Field>Company website<Input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://acme.com" /></Field>
              {step === 2 && <Field>Target NAICS codes<Input value={naics} onChange={e => setNaics(e.target.value)} placeholder="541511, 541512" /></Field>}
              {step === 2 && <Field>Target agencies<Input value={agencies} onChange={e => setAgencies(e.target.value)} placeholder="U.S. Air Force, DISA" /></Field>}
            </Grid>
          )}

          {step === 3 && (
            <>
              <Summary>
                <strong>{profile?.companyName || organization?.name}</strong>
                <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.55 }}>{profile?.companyDescription || 'Company description needs review in Profile.'}</p>
                <MetricGrid>
                  <Metric><span>Capabilities</span><strong>{summary.capabilities}</strong></Metric>
                  <Metric><span>Corporate PP</span><strong>{summary.corporatePastPerformance}</strong></Metric>
                  <Metric><span>Key personnel</span><strong>{summary.keyPersonnel}</strong></Metric>
                </MetricGrid>
              </Summary>
              <Grid style={{ marginTop: 16 }}>
                <Field>Company FCL<Input as="select" value={facilityClearance} onChange={e => setFacilityClearance(e.target.value)}><option value="unknown">Not verified</option><option value="none">No FCL</option><option value="secret">Active Secret FCL</option><option value="top secret">Active Top Secret FCL</option></Input></Field>
                <Field>Staff available for new work<Input type="number" min="0" value={availableStaff} onChange={e => setAvailableStaff(e.target.value)} placeholder="Leave blank if unknown" /></Field>
                <Field>Preferred role<Input as="select" value={preferredRole} onChange={e => setPreferredRole(e.target.value)}><option value="either">Prime or subcontractor</option><option value="prime">Prefer prime</option><option value="subcontractor">Prefer subcontractor</option></Input></Field>
                <Field>Operating limits<Input as="textarea" value={operatingLimitations} onChange={e => setOperatingLimitations(e.target.value)} placeholder="Staffing, location, travel, delivery, or teaming limits" /></Field>
              </Grid>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 18, fontSize: 13, lineHeight: 1.5, color: 'var(--text-1)' }}>
                <input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} style={{ marginTop: 3 }} />
                <span>I understand that generated company claims must be verified before they are used in a proposal or external communication.</span>
              </label>
            </>
          )}

          {error && <Notice $error>{error}</Notice>}
          {busy && <Notice>SAMKLR is working. Website analysis can take up to a minute.</Notice>}

          <Actions>
            <span style={{ color: 'var(--text-2)', fontSize: 11 }}>Private to your organization</span>
            {step === 1 && <Button disabled={busy || !companyName.trim()} onClick={createWorkspace}>Create workspace →</Button>}
            {step === 2 && <Button disabled={busy || !canBuild} onClick={buildProfile}>{busy ? 'Building…' : 'Build intelligence →'}</Button>}
            {step === 3 && <Button disabled={busy || !verified || !profile} onClick={launch}>Launch dashboard →</Button>}
          </Actions>
        </Card>
      </Shell>
    </Page>
  );
}
